import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, IconButton, InputAdornment,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { ClosedOutcome } from '../../api/types';
import { useHistoricalCount, useHistoricalOpportunities } from '../../api/hooks';
import { EmptyState, LoadingState, UserText } from '../common/Common';
import { formatCurrency, formatUtcDate } from '../common/format';
import { useThemeMode } from '../../theme/ThemeModeProvider';

type OutcomeFilter = 'all' | ClosedOutcome;

/** Deals won or lost more than six weeks ago. Deliberately an archive: collapsed on every load
 *  (the expansion state is not persisted) and not fetched at all until it is opened. */
export function HistoricalSection() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { stageColors } = useThemeMode();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [expanded, setExpanded] = useState(false);
  const [outcome, setOutcome] = useState<OutcomeFilter>('all');
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);

  const { data: totalCount } = useHistoricalCount();
  const query = {
    outcome: outcome === 'all' ? undefined : outcome,
    q: debounced || undefined,
    page,
  };
  const { data, isLoading } = useHistoricalOpportunities(query, expanded);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 300);
    return () => clearTimeout(timer);
  }, [term]);

  // Changing a filter while on page 3 would otherwise show an empty page.
  useEffect(() => setPage(1), [debounced, outcome]);

  const rows = data?.items ?? [];
  const pages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / (data?.pageSize || 25)));

  const outcomeChip = (value: ClosedOutcome | null) => {
    if (!value) return null;
    const color = value === 'lost' ? stageColors.lost : stageColors.won;
    return <Chip size="small" variant="outlined" label={t(`outcome.${value}`)} sx={{ color, borderColor: color }} />;
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
      disableGutters
      data-testid="historical-section"
      sx={{ mt: 2.5, borderRadius: '10px', '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="historical-toggle">
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h3">{t('historical.title')}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
            {t('historical.hint')}
          </Typography>
          {totalCount !== undefined && (
            <Chip size="small" label={totalCount} data-testid="historical-count" />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={outcome}
            onChange={(_, value: OutcomeFilter | null) => value && setOutcome(value)}
          >
            <ToggleButton value="all" data-testid="historical-all">{t('common.all')}</ToggleButton>
            <ToggleButton value="won" data-testid="historical-won">{t('outcome.won')}</ToggleButton>
            <ToggleButton value="lost" data-testid="historical-lost">{t('outcome.lost')}</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder={t('historical.filterPlaceholder')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'historical-filter' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
        </Stack>

        {isLoading && <LoadingState label={t('historical.loading')} />}

        {!isLoading && rows.length === 0 && (
          <EmptyState title={t('historical.empty')} description={t('historical.emptyDescription')} />
        )}

        {rows.length > 0 && (isPhone ? (
          // The table would force the page to scroll sideways on a phone, so the same rows are
          // stacked instead.
          <Stack spacing={1} data-testid="historical-list">
            {rows.map((row) => (
              <Box
                key={row.id}
                onClick={() => navigate(`/opportunities/${row.id}`)}
                data-testid={`historical-row-${row.id}`}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: '10px', cursor: 'pointer' }}
              >
                <Typography variant="h4" noWrap title={row.lostReason ?? undefined}>
                  <UserText>{row.title}</UserText>
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  <UserText>{row.customerName}</UserText>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                  {outcomeChip(row.closedOutcome)}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(row.estimatedValue, row.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatUtcDate(row.closedAtUtc)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" data-testid="historical-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('historical.columns.title')}</TableCell>
                  <TableCell>{t('historical.columns.customer')}</TableCell>
                  <TableCell>{t('historical.columns.outcome')}</TableCell>
                  <TableCell align="right">{t('historical.columns.value')}</TableCell>
                  <TableCell>{t('historical.columns.closed')}</TableCell>
                  {!isTablet && <TableCell>{t('historical.columns.owner')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/opportunities/${row.id}`)}
                    data-testid={`historical-row-${row.id}`}
                  >
                    <TableCell title={row.lostReason ?? undefined}><UserText>{row.title}</UserText></TableCell>
                    <TableCell><UserText>{row.customerName}</UserText></TableCell>
                    <TableCell>{outcomeChip(row.closedOutcome)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.estimatedValue, row.currency)}</TableCell>
                    <TableCell>{formatUtcDate(row.closedAtUtc)}</TableCell>
                    {!isTablet && <TableCell><UserText>{row.ownerName ?? '—'}</UserText></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}

        {pages > 1 && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label={t('historical.previousPage')}
              data-testid="historical-prev"
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {t('historical.pageOf', { page, pages })}
            </Typography>
            <IconButton
              size="small"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
              aria-label={t('historical.nextPage')}
              data-testid="historical-next"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
